import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import locationService from "../services/locationService";
import depProfileService from "../services/depProfileService";
import compressImageFile from "../utils/compressImageFile";

const emptyForm = {
  name: "",
  schoolNumber: "",
  defaultDepProfile: "",
  isPrimary: false,
  enrollBannerImage: "",
  deviceNameTemplate: "{{UserFullName}}",
};

const Locations = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState(emptyForm);
  const [depProfiles, setDepProfiles] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [bannerDirty, setBannerDirty] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate("/login");
    }
  }, [user, navigate]);

  useEffect(() => {
    loadLocations();
    depProfileService.list().then(setDepProfiles).catch(() => setDepProfiles([]));
  }, []);

  const loadLocations = async () => {
    try {
      setLoading(true);
      const data = await locationService.getLocations();
      setLocations(data);
    } catch (err) {
      toast.error("Locations konnten nicht geladen werden.");
      setLocations([]);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setEditingId(null);
    setBannerDirty(false);
  };

  const locationPayload = () => {
    const payload = {
      name: formData.name.trim(),
      schoolNumber: formData.schoolNumber.trim().toUpperCase(),
      defaultDepProfile: formData.defaultDepProfile || null,
      isPrimary: Boolean(formData.isPrimary),
      deviceNameTemplate: formData.deviceNameTemplate.trim() || "{{UserFullName}}",
    };
    if (bannerDirty) payload.enrollBannerImage = formData.enrollBannerImage || "";
    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.schoolNumber.trim()) {
      toast.error("Name und S-Nummer sind erforderlich.");
      return;
    }

    try {
      if (editingId) {
        await locationService.updateLocation(editingId, locationPayload());
        toast.success("Location aktualisiert.");
      } else {
        await locationService.createLocation(locationPayload());
        toast.success("Location angelegt.");
      }
      resetForm();
      loadLocations();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Fehler";
      toast.error(msg);
    }
  };

  const handleEdit = async (loc) => {
    setEditingId(loc._id);
    setBannerDirty(false);
    setFormData({
      name: loc.name,
      schoolNumber: loc.schoolNumber,
      defaultDepProfile: loc.defaultDepProfile?._id || loc.defaultDepProfile || "",
      isPrimary: Boolean(loc.isPrimary),
      enrollBannerImage: "",
      deviceNameTemplate: loc.deviceNameTemplate || "{{UserFullName}}",
    });
    try {
      const full = await locationService.getLocation(loc._id);
      setFormData({
        name: full.name,
        schoolNumber: full.schoolNumber,
        defaultDepProfile: full.defaultDepProfile?._id || full.defaultDepProfile || "",
        isPrimary: Boolean(full.isPrimary),
        enrollBannerImage: full.enrollBannerImage || "",
        deviceNameTemplate: full.deviceNameTemplate || "{{UserFullName}}",
      });
    } catch {
      toast.error("Banner konnte nicht geladen werden.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Location wirklich löschen?")) return;
    try {
      await locationService.deleteLocation(id);
      toast.success("Location gelöscht.");
      loadLocations();
      if (editingId === id) resetForm();
    } catch (err) {
      toast.error("Location konnte nicht gelöscht werden.");
    }
  };

  const handleBannerFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    try {
      const dataUrl = await compressImageFile(file, { maxEdge: 1600, quality: 0.8 });
      setFormData((prev) => ({ ...prev, enrollBannerImage: dataUrl }));
      setBannerDirty(true);
    } catch (err) {
      toast.error(err.message || "Bild konnte nicht übernommen werden.");
    }
  };

  if (loading) {
    return <p className="text-center my-4">Laden...</p>;
  }

  return (
    <div className="container">
      <h2 className="mb-4">Locations</h2>

      <form onSubmit={handleSubmit} className="card mb-4 p-3">
        <div className="row g-2">
          <div className="col-md-4">
            <label className="form-label">S-Nummer (z.B. S15)</label>
            <input
              type="text"
              className="form-control"
              placeholder="S15"
              value={formData.schoolNumber}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, schoolNumber: e.target.value }))
              }
            />
          </div>
          <div className="col-md-4">
            <label className="form-label">Name</label>
            <input
              type="text"
              className="form-control"
              placeholder="Grundschule Mitte"
              value={formData.name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
            />
          </div>
          <div className="col-md-8">
            <label className="form-label">Gerätenamen-Vorlage bei Benutzerzuweisung</label>
            <input
              type="text"
              className="form-control"
              placeholder="{{UserFullName}}"
              value={formData.deviceNameTemplate}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, deviceNameTemplate: e.target.value }))
              }
            />
            <div className="form-text">
              Platzhalter: {"{{Username}}"}, {"{{UserFullName}}"}, {"{{UserClass}}"}, {"{{UserLocation}}"}
            </div>
          </div>
          <div className="col-md-4">
            <label className="form-label">Standard-DEP-Profil</label>
            <select
              className="form-select"
              value={formData.defaultDepProfile}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, defaultDepProfile: e.target.value }))
              }
            >
              <option value="">keins</option>
              {depProfiles.map((profile) => (
                <option key={profile.id || profile._id} value={profile.id || profile._id}>
                  {profile.profile_name || profile.profile_uuid}
                </option>
              ))}
            </select>
          </div>
          <div className="col-12">
            <div className="form-check mt-2">
              <input
                id="location-is-primary"
                className="form-check-input"
                type="checkbox"
                checked={Boolean(formData.isPrimary)}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, isPrimary: e.target.checked }))
                }
              />
              <label className="form-check-label" htmlFor="location-is-primary">
                Hauptstandort (Enrollment-Banner, solange das Gerät keinem anderen Standort zugeordnet ist)
              </label>
            </div>
          </div>
          <div className="col-12 mt-2">
            <label className="form-label">Enrollment-Banner</label>
            <input
              type="file"
              className="form-control"
              accept="image/png,image/jpeg,.png,.jpg,.jpeg"
              onChange={handleBannerFile}
            />
            <div className="form-text">
              Wird auf der ADE-Anmeldeseite angezeigt. Ohne eigenes Bild gilt das Banner des Hauptstandorts (Medienzentrum).
            </div>
            {formData.enrollBannerImage ? (
              <div className="d-flex align-items-start gap-3 mt-2">
                <img
                  alt="Enrollment-Banner"
                  src={formData.enrollBannerImage}
                  className="rounded border"
                  style={{ maxWidth: 320, maxHeight: 120, objectFit: "cover" }}
                />
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => {
                    setFormData((prev) => ({ ...prev, enrollBannerImage: "" }));
                    setBannerDirty(true);
                  }}
                >
                  Banner entfernen
                </button>
              </div>
            ) : null}
          </div>
          <div className="col-md-4 d-flex align-items-end gap-2 mt-2">
            <button type="submit" className="btn btn-primary">
              {editingId ? "Aktualisieren" : "Hinzufügen"}
            </button>
            {editingId && (
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={resetForm}
              >
                Abbrechen
              </button>
            )}
          </div>
        </div>
      </form>

      <table className="table">
        <thead>
          <tr>
            <th>S-Nummer</th>
            <th>Name</th>
            <th>Standard-DEP-Profil</th>
            <th>Enrollment</th>
            <th style={{ width: "120px" }}></th>
          </tr>
        </thead>
        <tbody>
          {locations.map((loc) => (
            <tr key={loc._id}>
              <td>{loc.schoolNumber}</td>
              <td>
                {loc.name}
                {loc.isPrimary ? (
                  <span className="badge text-bg-primary ms-2">Hauptstandort</span>
                ) : null}
              </td>
              <td>{loc.defaultDepProfile?.profile_name || loc.defaultDepProfile?.profile_uuid || "—"}</td>
              <td>{loc.hasEnrollBanner ? "Banner" : "—"}</td>
              <td>
                <button
                  className="btn btn-sm btn-outline-primary me-1"
                  onClick={() => handleEdit(loc)}
                >
                  Bearbeiten
                </button>
                <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => handleDelete(loc._id)}
                >
                  Löschen
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {locations.length === 0 && (
        <p className="text-muted">Noch keine Locations angelegt.</p>
      )}
    </div>
  );
};

export default Locations;
